import * as XLSX from 'xlsx-js-style'
import { ImportPriceField, ProductPricesTemplateColumn } from '../../../../features/importer'
import { ExcelFormats, ExcelStyles } from '../../../../features/csv-downloader'
import { usePricesHooks } from '../../../operations'
import { useProductLoaders } from '../../loaders/productLoaders'
import { useProductPricesLoaders } from '../../loaders/productPricesLoaders'
import { ProductPrices } from '../../state/types'
import { O } from '../../../../features/ui'
import { useSupplierLoaders } from '../../../purchases'

// Filas de información arriba de la tabla; el importador detecta la cabecera solo
const HEADER_ROW = 4

const fieldLabels: Record<ImportPriceField, string> = {
    [ImportPriceField.PURCHASE_PRICE]: 'Precio de Compra',
    [ImportPriceField.PRICE_YIELD]: 'Rentabilidad',
    [ImportPriceField.PRICE]: 'Precio de Venta',
    [ImportPriceField.PRICE_VAT]: 'Precio de Venta c/IVA'
}

export const useDownloadPricesTemplate = () => {
    const pricesHooks = usePricesHooks()
    const productLoaders = useProductLoaders()
    const productPricesLoaders = useProductPricesLoaders()
    const supplierLoaders = useSupplierLoaders()

    const getCurrentValue = (priceField: ImportPriceField, productId: string, prices: ProductPrices): O<number> => {
        try {
            if (priceField === ImportPriceField.PURCHASE_PRICE) {
                return prices.purchasePrice
            } else if (priceField === ImportPriceField.PRICE_YIELD) {
                const saleYield = prices.saleYield ?? pricesHooks.saleYield(prices.purchasePrice, pricesHooks.product.basePrice(productId))
                return saleYield / 100
            } else {
                return pricesHooks.product.basePrice(productId, priceField === ImportPriceField.PRICE_VAT)
            }
        } catch {
            return undefined
        }
    }

    return async (priceField: ImportPriceField) => {
        const isYield = priceField === ImportPriceField.PRICE_YIELD
        const valueFormat = isYield ? ExcelFormats.Percentage : ExcelFormats.Accounting
        const newValueHeader = ProductPricesTemplateColumn[priceField]

        const [products, productPricesList] = await Promise.all([
            productLoaders().loadActiveProducts(),
            productPricesLoaders().loadAllProductPrices()
        ])
        const rows = products
            .map(product => {
                const prices = productPricesList.find(prices => prices.productId === product.id)
                const currentValue = prices ? getCurrentValue(priceField, product.id, prices) : undefined
                return { product, currentValue }
            })
            .filter(row => row.currentValue !== undefined)
            .sort((a, b) => (a.product.name || '').localeCompare(b.product.name || '', undefined, { numeric: true, sensitivity: 'base' }))

        // Para filtrar por proveedor al cargar la lista de precios que manda cada uno
        const supplierIds = Array.from(new Set(rows.map(row => row.product.supplierId).filter(id => !!id)))
        const suppliers = await supplierLoaders().loadSuppliers(supplierIds)
        const supplierNames = new Map(suppliers.map(supplier => [supplier.id, supplier.name]))

        const metadataRows = [
            ['Campo a actualizar', fieldLabels[priceField]],
            ['Fecha', new Date().toLocaleDateString('es-AR')],
            [],
            [`Completá solo la columna "${newValueHeader}". Las filas vacías se ignoran.`]
        ]
        const headerRow = [
            ProductPricesTemplateColumn.SUPPLIER_CODE,
            ProductPricesTemplateColumn.PRODUCT,
            ProductPricesTemplateColumn.SUPPLIER,
            `${newValueHeader} Actual`,
            newValueHeader,
            'Variación'
        ]

        const ws = XLSX.utils.aoa_to_sheet([...metadataRows, headerRow])

        metadataRows.slice(0, 2).forEach((_, rowIndex) => {
            ws[XLSX.utils.encode_cell({ r: rowIndex, c: 0 })].s = ExcelStyles.metadataTitle
            ws[XLSX.utils.encode_cell({ r: rowIndex, c: 1 })].s = ExcelStyles.metadataValue
        })
        ws['A4'].s = ExcelStyles.hint
        headerRow.forEach((_, colIndex) => {
            ws[XLSX.utils.encode_cell({ r: HEADER_ROW, c: colIndex })].s = ExcelStyles.header
        })

        rows.forEach(({ product, currentValue }, index) => {
            const excelRow = HEADER_ROW + index + 2
            const currentCell = `D${excelRow}`
            const newCell = `E${excelRow}`
            // Rentabilidad: diferencia en puntos; precios: variación porcentual. Solo de referencia, el importador no la lee
            const variationFormula = isYield ?
                `IF(${newCell}="","",${newCell}-${currentCell})` :
                `IF(OR(${newCell}="",${currentCell}=0),"",${newCell}/${currentCell}-1)`

            const rowData = [
                { v: product.supplierCode || '', t: 's', s: ExcelStyles.greyRow },
                { v: product.name || '', t: 's', s: ExcelStyles.greyRow },
                { v: supplierNames.get(product.supplierId) || '', t: 's', s: ExcelStyles.greyRow },
                { v: currentValue, t: 'n', z: valueFormat, s: ExcelStyles.greyRow },
                { v: '', t: 's', z: valueFormat, s: ExcelStyles.editable },
                { f: variationFormula, t: 'n', z: ExcelFormats.Percentage, s: ExcelStyles.greyRow }
            ]
            XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: `A${excelRow}` })
        })

        ws['!cols'] = [{ wch: 18 }, { wch: 45 }, { wch: 25 }, { wch: 22 }, { wch: 22 }, { wch: 12 }]
        ws['!rows'] = [{}, {}, {}, {}, { hpt: 30 }]
        ws['!autofilter'] = { ref: XLSX.utils.encode_range({
            s: { r: HEADER_ROW, c: 0 },
            e: { r: HEADER_ROW + rows.length, c: headerRow.length - 1 }
        }) }

        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Actualizar Precios')
        XLSX.writeFile(wb, `Actualizar ${fieldLabels[priceField]}.xlsx`)
    }
}
